import { FC, useEffect, useState } from "react";
import {
  Banner,
  ButtonWO,
  ErrorText,
  PageContainer,
  PageWrapper,
  Paragraph,
} from "../shared.styled";
import logo from "../../assets/images/logo.svg";
import { Device, IDataSource } from "../../api/models";
import { getJWT, get, post, patch, del } from "../../api/api";
import {
  ScrollArea,
  SearchInput,
  SearchItem,
  SearchableList,
  OptionSelection,
  OptionsList,
  LightOptionButton,
  DeviceOptions,
  DeviceOption,
  OptionList,
  DeviceOptionTitle,
  DeleteDevice,
  Tabs,
  TabButtons,
  TabButton,
  Tab,
  Options,
  Option,
} from "./setup.styled";
import { CheckCircleFilled, DeleteOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import LocationsMap from "./LocationsMap";
import UnitSelection from "./UnitSelection";

const Setup: FC = (): JSX.Element => {
  const [deviceId, setDeviceId] = useState<string>("");
  const [tab, setTab] = useState<string>("list");
  const [deleteDevice, setDeleteDevice] = useState<string>("");
  const [device, setDevice] = useState<Device>();
  const [options, setOptions] = useState<IDataSource[]>([]);
  const [otherDevices, setOtherDevices] = useState<
    [{ deviceId: string; createdAt: Date }] | null
  >(null);
  const [selected, setSelected] = useState<IDataSource[]>([]);
  const [warning, setWarning] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [LEDBrightness, setLEDBrightness] = useState<number>(255);
  const [units, setUnits] = useState<string>("kph");
  const navigate = useNavigate();

  useEffect(() => {
    const JWT = getJWT();
    if (JWT && JWT.deviceId) {
      setDeviceId(JWT.deviceId);
    }
  }, []);

  useEffect(() => {
    const getDevice = async () => {
      if (deviceId) {
        try {
          const device = await get(`/device/${deviceId}`);
          if (device.data) {
            setDevice(device.data);
          }
        } catch (e) {
          // redirect to login
          navigate("/login");
        }
      } else {
        setTimeout(getDevice, 500);
      }
    };
    getDevice();
  }, [deviceId]);

  useEffect(() => {
    if (device) {
      get(`/device/${deviceId}/others`).then((res) => {
        if (res.data && res.data.length > 1) {
          setOtherDevices(res.data);
        }
        if (device.dataSources) {
          setSelected(device.dataSources);
        }
        void fetchOptions();
      });

      setLEDBrightness(device.LEDBrightness);
      setUnits(device.units);
    }
  }, [device]);

  useEffect(() => {
    if (device?.dataSourceName === "surf" && search.length > 2) {
      fetchOptions(search);
    }
  }, [search]);

  const fetchOptions = async (terms?: string) => {
    const res = await get(
      `/data-source/options?deviceId=${deviceId}${
        terms ? `&terms=${terms}` : ""
      }`
    );
    setOptions(res.data);
  };

  const handleSelect = (sourceId: string) => {
    if (!selected.some((s) => s.sourceId === sourceId)) {
      if (device && selected.length < device.sourceLimit) {
        console.log("select");
        const selectedOption = options.find((o) => o.sourceId === sourceId);
        if (selectedOption) {
          setSelected([...selected, selectedOption]);
        }
      } else if (device) {
        setWarning(
          `You've selected your maximum of ${device.sourceLimit} sites.`
        );
        setTimeout(() => setWarning(""), 3000);
      }
    } else {
      console.log("un-select");

      setSelected(selected.filter((s) => s.sourceId !== sourceId));
    }
    setSearch("");
  };

  const handleSave = async () => {
    await post(`/data-source/${deviceId}`, {
      sources: selected,
    });
    if (device) {
      await patch(`/device/${deviceId}`, {
        LEDBrightness,
        units,
      });
    }
    navigate(`/saved/${deviceId}`);
  };

  const changeDevice = (deviceId: string) => {
    setSelected([]);
    setDeviceId(deviceId);
  };

  const deleteDeviceRecord = async () => {
    del(`/device/${deleteDevice}`);
    setDeleteDevice("");
    if (otherDevices) {
      const newDeviceList = otherDevices.filter(
        (d) => d.deviceId !== deleteDevice
      ) as [{ deviceId: string; createdAt: Date }];
      setOtherDevices(newDeviceList);
    }
  };
  return (
    <PageWrapper>
      <PageContainer style={{ textAlign: "center" }}>
        <Banner>
          <img src={logo} alt="logo" width="260" />
        </Banner>
        {otherDevices && (
          <DeviceOptions>
            <Paragraph>Pick A Different Device</Paragraph>
            <OptionList>
              {otherDevices
                .sort(
                  (a, b) =>
                    moment(a.createdAt).unix() - moment(b.createdAt).unix()
                )
                .map((d) => (
                  <DeviceOption
                    key={d.deviceId}
                    selected={d.deviceId === deviceId}
                    onClick={() => changeDevice(d.deviceId)}
                  >
                    <DeleteDevice
                      onClick={() =>
                        d.deviceId === deleteDevice
                          ? deleteDeviceRecord()
                          : setDeleteDevice(d.deviceId)
                      }
                    >
                      {d.deviceId === deleteDevice ? (
                        `Delete?`
                      ) : (
                        <DeleteOutlined />
                      )}
                    </DeleteDevice>
                    <DeviceOptionTitle>
                      {moment(d.createdAt).format("MMM Do YY")}
                    </DeviceOptionTitle>
                  </DeviceOption>
                ))}
            </OptionList>
          </DeviceOptions>
        )}
        <Paragraph>Select the sites you'd like on your device.</Paragraph>
        {device && (
          <Paragraph>
            We'll only ever show the top {device.sourceListLimit} sites on your
            device.
          </Paragraph>
        )}
        {selected.length > 0 && (
          <Paragraph style={{ marginTop: 20 }}>
            {selected.map((option, i) => (
              <OptionSelection
                key={i}
                onClick={() => handleSelect(option.sourceId)}
              >
                {option.title} <DeleteOutlined />
              </OptionSelection>
            ))}
          </Paragraph>
        )}
        {warning && <ErrorText>{warning}</ErrorText>}
        <Tabs>
          <TabButtons>
            <TabButton selected={tab === "list"} onClick={() => setTab("list")}>
              List
            </TabButton>
            <TabButton selected={tab === "map"} onClick={() => setTab("map")}>
              Map
            </TabButton>
          </TabButtons>
          <Tab selected={tab === "map"}>
            <LocationsMap
              handleSelect={handleSelect}
              options={options.filter(
                (loc) => loc.longitude && loc.latitude && !loc.closed
              )}
              selected={selected}
            />
          </Tab>
          <Tab selected={tab === "list"}>
            <SearchableList>
              <SearchInput
                value={search}
                placeholder="Search..."
                onChange={(e) => setSearch(e.target.value)}
              />
              <ScrollArea>
                {options
                  .filter(
                    (o) =>
                      (!search.trim() && !o.closed) ||
                      (search.trim() &&
                        o.title
                          .toLowerCase()
                          .includes(search.trim().toLowerCase()))
                  )
                  .sort((a, b) => a.title.localeCompare(b.title))
                  .map((option) => (
                    <SearchItem
                      key={option.sourceId}
                      onClick={() => handleSelect(option.sourceId)}
                    >
                      {option.title}
                      {selected.some((s) => s.sourceId === option.sourceId) && (
                        <CheckCircleFilled />
                      )}
                    </SearchItem>
                  ))}
              </ScrollArea>
            </SearchableList>
          </Tab>
        </Tabs>

        <Options>
          <Option>
            <Paragraph style={{ padding: "30px 0 0 0" }}>
              Flyable Notification Light Brightness
            </Paragraph>
            <OptionsList>
              <LightOptionButton
                level={0}
                selected={LEDBrightness === 0}
                onClick={() => setLEDBrightness(0)}
              ></LightOptionButton>
              <LightOptionButton
                level={25}
                selected={LEDBrightness === 1}
                onClick={() => setLEDBrightness(1)}
              ></LightOptionButton>
              <LightOptionButton
                level={50}
                selected={LEDBrightness === 10}
                onClick={() => setLEDBrightness(10)}
              ></LightOptionButton>
              <LightOptionButton
                level={100}
                selected={LEDBrightness === 255}
                onClick={() => setLEDBrightness(255)}
              ></LightOptionButton>
            </OptionsList>
          </Option>
          <Option>
            <Paragraph style={{ padding: "30px 0 0 0" }}>
              Wind Speed Units
            </Paragraph>
            <OptionsList>
              <UnitSelection units={units} setUnits={setUnits} />
            </OptionsList>
          </Option>
        </Options>

        <ButtonWO style={{ marginTop: 20 }} onClick={handleSave}>
          Save
        </ButtonWO>
      </PageContainer>
    </PageWrapper>
  );
};
export default Setup;
